const MAX_CENTS = 999999999999
const MAX_QUANTITY = 2147483647

export function parseCents(value) {
  const text = String(value ?? '').trim().replace(',', '.')
  if (!/^\d{1,10}(?:\.\d{1,2})?$/.test(text)) return NaN
  const [whole, fraction = ''] = text.split('.')
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
  return Number.isSafeInteger(cents) && cents <= MAX_CENTS ? cents : NaN
}

export function centsToMoney(cents) {
  if (!Number.isSafeInteger(cents) || cents < 0) return ''
  return `${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`
}

export function formatCurrency(value) {
  const amount = value == null || value === '' ? NaN : Number(String(value).replace(',', '.'))
  return Number.isFinite(amount) && amount >= 0
    ? new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 2 }).format(amount)
    : '—'
}

export function actorName(actor) {
  return actor?.full_name || actor?.username || 'Не указан'
}

export function saleTotalCents(lines) {
  let total = 0
  for (const line of lines) {
    const price = parseCents(line.unit_price)
    const quantity = Number(line.quantity)
    if (!Number.isFinite(price) || !Number.isInteger(quantity) || quantity < 1 || quantity > MAX_QUANTITY) return NaN
    total += price * quantity
    if (!Number.isSafeInteger(total) || total > MAX_CENTS) return NaN
  }
  return total
}

export function validateSale(form, catalog) {
  if (!Array.isArray(form.lines) || !form.lines.length) return 'Добавьте товар или услугу.'
  if (form.lines.length > 100) return 'В одной продаже допустимо не более 100 позиций.'
  const seen = new Set()
  let priceChanged = false
  for (const line of form.lines) {
    const item = catalog.find((entry) => String(entry.id) === String(line.item_id))
    if (!item || item.is_active === false) return 'Выберите активный товар или услугу.'
    if (seen.has(item.id)) return 'Объедините одинаковые позиции в одну строку.'
    seen.add(item.id)
    const quantity = Number(line.quantity)
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > MAX_QUANTITY) return 'Количество должно быть положительным целым числом.'
    if (item.kind === 'product' && quantity > Number(item.stock_quantity || 0)) return `Недостаточно товара «${item.name}» на складе.`
    const price = parseCents(line.unit_price)
    if (!Number.isFinite(price)) return 'Укажите цену от 0 до 9 999 999 999,99 ₽, не более двух знаков после запятой.'
    if (price !== parseCents(item.unit_price)) priceChanged = true
  }
  if (!Number.isFinite(saleTotalCents(form.lines))) return 'Сумма продажи превышает допустимое значение.'
  if (priceChanged && !form.notes?.trim()) return 'Укажите причину изменения цены относительно справочника.'
  return ''
}

export function serializeSale(form, bookingId = null) {
  return {
    ...(bookingId != null ? { booking_id: Number(bookingId) } : {}),
    lines: form.lines.map((line) => ({
      item_id: Number(line.item_id),
      quantity: Number(line.quantity),
      unit_price: centsToMoney(parseCents(line.unit_price)),
    })),
    notes: form.notes?.trim() || '',
  }
}

export function validateCatalogItem(form, kind, editing = false) {
  if (!form.name?.trim()) return 'Введите название.'
  if (Array.from(form.name.trim()).length > 160) return 'Название должно быть не длиннее 160 символов.'
  if (Array.from(form.sku?.trim() || '').length > 80) return 'Артикул должен быть не длиннее 80 символов.'
  if (!form.unit?.trim() || Array.from(form.unit.trim()).length > 32) return 'Укажите единицу измерения до 32 символов.'
  if (!Number.isFinite(parseCents(form.unit_price))) return 'Укажите корректную цену с точностью до копейки.'
  if (!editing && kind === 'product') {
    const quantity = Number(form.initial_quantity || 0)
    if (!Number.isInteger(quantity) || quantity < 0 || quantity > MAX_QUANTITY) return 'Начальный остаток должен быть целым неотрицательным числом.'
  }
  if (/[\u0000-\u001f\u007f]/u.test(`${form.name}${form.sku || ''}${form.unit}`)) return 'Поля не должны содержать управляющие символы.'
  return ''
}

export function serializeCatalogItem(form, kind, editing = false) {
  return {
    ...(!editing ? { kind } : {}),
    name: form.name.trim(),
    sku: form.sku?.trim() || null,
    unit: form.unit.trim(),
    unit_price: centsToMoney(parseCents(form.unit_price)),
    ...(!editing && kind === 'product' ? { initial_quantity: Number(form.initial_quantity || 0) } : {}),
  }
}

export const movementLabels = {
  opening: 'Начальный остаток', receipt: 'Приход', write_off: 'Списание', sale: 'Продажа', sale_cancel: 'Отмена продажи',
}

export function sumActiveSales(sales) {
  let total = 0
  for (const sale of sales) {
    if (sale.status === 'cancelled') continue
    const cents = parseCents(sale.total_amount)
    if (Number.isFinite(cents)) total += cents
    if (!Number.isSafeInteger(total)) return NaN
  }
  return total
}
